import { useState } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Wand2, Copy, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";

const DEFAULT_PHONE = "+919999611551";

export function AdminWaTestSendPanel({ onSent }) {
  const [open, setOpen] = useState(false);

  // Form fields
  const [targetPhone, setTargetPhone] = useState(DEFAULT_PHONE);
  const [templateName, setTemplateName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [downloadStage, setDownloadStage] = useState(0);
  const [ctaDestination, setCtaDestination] = useState("upgrade");
  const [variablesText, setVariablesText] = useState(""); // JSON-encoded array string

  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {wa_token, cta_url, default_template_variables, expires_at}

  // Send state
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const parseVariables = () => {
    const t = variablesText.trim();
    if (!t) return null;
    try {
      const v = JSON.parse(t);
      if (!Array.isArray(v)) throw new Error("Not an array");
      return v.map((x) => String(x));
    } catch {
      return undefined; // signal invalid
    }
  };

  const buildPayload = () => ({
    target_phone: targetPhone.trim(),
    template_name: templateName.trim(),
    first_name: firstName.trim(),
    download_stage: Number(downloadStage),
    cta_destination: ctaDestination,
  });

  const handlePreview = async () => {
    if (!targetPhone.trim()) {
      toast.error("Recipient phone is required");
      return;
    }
    setPreviewLoading(true);
    setPreview(null);
    setSendResult(null);
    try {
      const res = await adminAPI.waTestSendPreview(buildPayload());
      setPreview(res.data);
      setVariablesText(JSON.stringify(res.data.default_template_variables));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!templateName.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!targetPhone.trim()) {
      toast.error("Recipient phone is required");
      return;
    }
    const override = parseVariables();
    if (override === undefined) {
      toast.error("templateVariables must be a valid JSON array, e.g. [\"Rahul\",\"<token>\"]");
      return;
    }

    setSending(true);
    setSendResult(null);
    try {
      const payload = {
        ...buildPayload(),
        template_variables_override: override, // null = use server default
      };
      const res = await adminAPI.waTestSend(payload);
      setSendResult(res.data);
      const ok = res.data?.send_result?.success;
      if (ok) {
        toast.success(`Sent to ${targetPhone}`);
        if (onSent) onSent();
      } else {
        const err = res.data?.send_result?.error || res.data?.send_result?.response || "Send failed";
        toast.error(`WASimple: ${err}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally {
      setSending(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="wa-test-send-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
        data-testid="wa-test-send-toggle"
      >
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-[#18181B]">Test WA Send</span>
          <span className="text-[11px] text-slate-500">Send a single template to your own phone</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="p-4 border-t border-slate-200 space-y-3" data-testid="wa-test-send-form">
          {/* Row 1: phone + template */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldText
              label="Recipient phone"
              value={targetPhone}
              onChange={setTargetPhone}
              placeholder="+919999611551"
              testid="wa-test-phone"
            />
            <FieldText
              label="Template name"
              value={templateName}
              onChange={setTemplateName}
              placeholder="fp_0_download_msg1"
              testid="wa-test-template"
            />
          </div>

          {/* Row 2: first_name + stage + cta */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldText
              label="first_name (body var)"
              value={firstName}
              onChange={setFirstName}
              placeholder="Rahul"
              testid="wa-test-firstname"
            />
            <FieldSelect
              label="Download stage"
              value={String(downloadStage)}
              onChange={(v) => setDownloadStage(Number(v))}
              options={[
                { value: "0", label: "0 downloads" },
                { value: "1", label: "1 download" },
                { value: "2", label: "2 downloads" },
              ]}
              testid="wa-test-stage"
            />
            <FieldSelect
              label="CTA destination"
              value={ctaDestination}
              onChange={setCtaDestination}
              options={[
                { value: "upgrade", label: "Upgrade popup" },
                { value: "library", label: "Library (dashboard)" },
              ]}
              testid="wa-test-cta"
            />
          </div>

          {/* Preview button */}
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={previewLoading}
              data-testid="wa-test-preview-btn"
            >
              {previewLoading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Generate Preview
            </Button>
            <span className="text-[11px] text-slate-500">
              Mints a wa_token + cta_url. Required before sending.
            </span>
          </div>

          {/* Preview block */}
          {preview && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2" data-testid="wa-test-preview">
              <PreviewRow label="wa_token" value={preview.wa_token} mono onCopy={copy} />
              <PreviewRow label="cta_url" value={preview.cta_url} mono onCopy={copy} />
              <PreviewRow
                label="expires_at"
                value={preview.expires_at}
                onCopy={copy}
              />

              {/* Editable variables */}
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  templateVariables (JSON array — editable)
                </label>
                <textarea
                  value={variablesText}
                  onChange={(e) => setVariablesText(e.target.value)}
                  rows={2}
                  className="w-full px-2.5 py-1.5 rounded-md text-xs font-mono bg-white border border-slate-200 outline-none focus:border-amber-500/50"
                  data-testid="wa-test-variables"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Default convention: <code>[first_name, wa_token]</code>. Adjust to match your approved template's
                  placeholder order.
                </p>
              </div>

              {/* Send button */}
              <div className="flex justify-end pt-1">
                <Button
                  onClick={handleSend}
                  disabled={sending || !templateName.trim()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                  data-testid="wa-test-send-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5 mr-1.5" /> Send Test WA
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Send result */}
          {sendResult && (
            <div
              className={`rounded-lg border p-3 text-xs ${
                sendResult.send_result?.success
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50"
              }`}
              data-testid="wa-test-send-result"
            >
              <div className="flex items-center gap-1.5 mb-2 font-semibold">
                {sendResult.send_result?.success ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-emerald-700">Sent successfully</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-red-700">Send failed</span>
                  </>
                )}
                <span className="text-slate-500 font-normal ml-2">
                  HTTP {sendResult.send_result?.status_code ?? "—"}
                </span>
              </div>
              <pre className="font-mono text-[10px] whitespace-pre-wrap break-all text-slate-700 max-h-32 overflow-auto">
                {JSON.stringify(
                  {
                    sent_variables: sendResult.sent_variables,
                    wasimple_response:
                      sendResult.send_result?.response || sendResult.send_result?.error,
                  },
                  null,
                  2
                )}
              </pre>
              <p className="text-[11px] text-slate-600 mt-2">
                Open the cta_url on your phone (or copy and tap). Auto-login + tracking events should
                appear in <strong>Recent Events</strong> below within seconds.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 px-2.5 rounded-md text-xs bg-white border border-slate-200 outline-none focus:border-amber-500/50"
        data-testid={testid}
      />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-2 rounded-md text-xs bg-white border border-slate-200 outline-none focus:border-amber-500/50"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function PreviewRow({ label, value, mono, onCopy }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[11px] font-medium text-slate-600 w-20 shrink-0 pt-0.5">{label}</span>
      <code
        className={`flex-1 px-2 py-1 rounded bg-white border border-amber-200 text-[11px] break-all ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </code>
      <button
        onClick={() => onCopy(value, label)}
        className="shrink-0 p-1 rounded hover:bg-amber-100 transition-colors"
        title="Copy"
      >
        <Copy className="h-3 w-3 text-slate-500" />
      </button>
    </div>
  );
}
